import React from 'react';
import { InternalApiEntityGroup, InternalApiSegmentEventType } from '../../../api-internal';
import { useQuerySegmentEvents } from './segmentEventActions';
import { showFeedback } from '../../../feedback/feedbackReducer';
import { useDispatch } from 'react-redux';
import { SalvFieldHeader, SalvProgressCircle, SalvProgressStepper, Step } from '@salvapp/ui-components';
import {
  FullCalculationFinishedEventContent,
  FullCalculationStartedEventContent,
  SegmentEvent,
  SingleCalculationFailedEventContent,
} from './segmentEventType';
import dayjs from 'dayjs';
import { formatDate } from '../../../common/datetime/FormattedDateTime';
import { StepType } from '@salvapp/ui-components/build/SalvProgressStepper/SalvProgressStepper.types';

interface Props {
  segment: InternalApiEntityGroup;
}

const SegmentEvents = ({ segment }: Props) => {
  const { code, fullCalculationState } = segment;
  const dispatch = useDispatch();
  const { data: segmentEvents, isPending, isError } = useQuerySegmentEvents({ code, fullCalculationState });

  if (isError) {
    dispatch(
      showFeedback({
        message: 'Could not load Segment Events',
        success: false,
      })
    );
    return <div />;
  }

  return (
    <div className={'calculation-log'}>
      <SalvFieldHeader label={'Calculation Log'} description={'Showing 10 latest entries'} className={'m-b-16'} />
      {isPending ? (
        <SalvProgressCircle ariaLabel={'Loading Segment Events'} />
      ) : (
        <SalvProgressStepper>
          {(segmentEvents as SegmentEvent[])
            .toSorted((a, b) => (dayjs(a.createdAt).isBefore(dayjs(b.createdAt)) ? 1 : -1))
            .map((event, index) => {
              const name = getEventName(event);
              const details = getEventDetails(event);
              const stepperType = getStepperType(event);
              const errorMessage = getErrorMessage(event);
              return (
                <Step
                  key={index}
                  header={name + (details ? ': ' + details : '')}
                  description={formatDate(event.createdAt, true)}
                  type={stepperType}
                >
                  {errorMessage && (
                    <div className='error-messages' key={index}>
                      {errorMessage}
                    </div>
                  )}
                </Step>
              );
            })}
        </SalvProgressStepper>
      )}
    </div>
  );
};

export default SegmentEvents;

const getStepperType = (event: SegmentEvent): StepType => {
  switch (event.eventType) {
    case InternalApiSegmentEventType.FULL_CALCULATION_FINISHED:
      return 'filled';
    case InternalApiSegmentEventType.FULL_CALCULATION_FAILED:
    case InternalApiSegmentEventType.SINGLE_CALCULATION_FAILED:
      return 'error';
    case InternalApiSegmentEventType.FULL_CALCULATION_STARTED:
    case InternalApiSegmentEventType.FULL_CALCULATION_CHECKPOINT_REACHED:
      return 'pending';
  }
};

const getEventName = (event: SegmentEvent): string => {
  switch (event.eventType) {
    case InternalApiSegmentEventType.FULL_CALCULATION_STARTED:
      return 'Full Calculation Started';
    case InternalApiSegmentEventType.FULL_CALCULATION_CHECKPOINT_REACHED:
      return 'Full Calculation Checkpoint Reached';
    case InternalApiSegmentEventType.FULL_CALCULATION_FINISHED:
      return 'Full Calculation Finished';
    case InternalApiSegmentEventType.FULL_CALCULATION_FAILED:
      return 'Full Calculation Failed';
    case InternalApiSegmentEventType.SINGLE_CALCULATION_FAILED:
      return 'Single Calculation Failed';
  }
};

const getEventDetails = (event: SegmentEvent): string => {
  const { eventType, eventContent } = event;
  switch (eventType) {
    case InternalApiSegmentEventType.FULL_CALCULATION_STARTED:
      return `${getFullCalculationStartedTriggerName(eventContent)} ${getFullCalculationStartTime(eventContent)}`;
    case InternalApiSegmentEventType.FULL_CALCULATION_CHECKPOINT_REACHED:
      return `${eventContent.entityCount} Entities processed`;
    case InternalApiSegmentEventType.FULL_CALCULATION_FINISHED:
      return `${eventContent.reason} ${getFullCalculationStartTime(eventContent)}`;
    case InternalApiSegmentEventType.FULL_CALCULATION_FAILED:
      return '';
    case InternalApiSegmentEventType.SINGLE_CALCULATION_FAILED:
      return getSingleCalculationFailedTriggerName(eventContent);
  }
};

const getErrorMessage = (event: SegmentEvent): string => {
  const { eventType, eventContent } = event;
  switch (eventType) {
    case InternalApiSegmentEventType.FULL_CALCULATION_FAILED:
      return eventContent.reason;
    case InternalApiSegmentEventType.SINGLE_CALCULATION_FAILED:
      return `Entity ${eventContent.entityExternalId}: ${eventContent.reason}`;
    default:
      return '';
  }
};

const getFullCalculationStartedTriggerName = (eventContent: FullCalculationStartedEventContent): string => {
  switch (eventContent.trigger) {
    case 'SEGMENT_CREATED':
      return 'Segment Created';
    case 'QUERY_TEMPLATES_UPDATED':
      return 'Query Templates Updated';
    case 'MANUAL':
      return 'Manual';
  }
};

const getFullCalculationStartTime = (
  eventContent: FullCalculationStartedEventContent | FullCalculationFinishedEventContent
): string => {
  if (!eventContent.startTime) {
    return '';
  } else {
    return '(Entities included in segment from: ' + formatDate(dayjs(eventContent.startTime), true) + ')';
  }
};

const getSingleCalculationFailedTriggerName = (eventContent: SingleCalculationFailedEventContent): string => {
  switch (eventContent.trigger) {
    case 'ENTITY_CREATED':
      return 'Entity Created';
    case 'ENTITY_UPDATED':
      return 'Entity Updated';
    case 'PERSON_STATUS_UPDATED':
      return 'Person Status Updated';
    case 'PERSON_TAG_ADDED':
      return 'Person Tag Added';
    case 'PERSON_TAG_REMOVED':
      return 'Person Tag Removed';
    case 'FINAL_RISK_UPDATED':
      return 'Final Risk Updated';
  }
};
